<template>
  <div>
    <iframe
      :src="`scorm/${course_id}/${executableFile}`"
      class="scorm-player"
    ></iframe>
  </div>
</template>

<script>
import "scorm-again";

export default {
  props: ["course_id", "scormProgress", "executableFile"],
  data() {
    return {
      status: 0,
      _lastActivity: 0,
      idleInterval: null,
      settings: {
        autocommit: true,
        autocommitSeconds: 5,
        dataCommitFormat: "flattened",
        lmsCommitUrl:
          process.env.VUE_APP_API_URL +
          "/employees/scorm-progress?courseId=" +
          this.course_id,
        xhrHeaders: {
          authorization: "Bearer " + localStorage.getItem("hot-token"),
          "content-type": "application/json",
        },
        responseHandler: (data) => {
          const r = JSON.parse(data.response);
          this.status = r.status;
          if (r.scormPassed === 1) {
            this.submitEmployeeData();
          }
        },
      },
    };
  },
  created: function () {
      window.API = new Scorm12API(this.settings);

      let keyValues = [];
      this.scormProgress.forEach(data => {
          keyValues[data.key] = data.value;
      });

      window.API.cmi.suspend_data = keyValues['cmi.suspend_data'];
  },
  mounted() {
    window.addEventListener("message", this._msgHandler);

    if (!window.API) {
      window.API = {
        LMSInitialize: () => "true",
        LMSFinish: () => "true",
        LMSGetValue: () => "",
        LMSSetValue: () => "true",
        LMSCommit: () => "true",
        LMSGetLastError: () => "0",
        LMSGetErrorString: () => "No error",
        LMSGetDiagnostic: () => "No diagnostic",
        cmi: { core: { score: { raw: 100 } } },
      };
      console.warn(
        "⚠️ Dummy SCORM API injected for local testing. No real tracking will happen."
      );
    }

    this.ensureSDKsLoaded();
    const outer = this.$el.querySelector(".scorm-player");
    if (outer) this.watchIframeTree(outer);
  },
  beforeDestroy() {
    window.removeEventListener("message", this._msgHandler);
    if (this.idleInterval) {
      clearInterval(this.idleInterval);
      this.idleInterval = null;
    }
  },
  methods: {
    _msgHandler(e) {
      const { platform, action, id } = e.data;
      console.log("Parent got:", platform, action, id);

      if (action === "play" && !this.idleInterval) {
        this.idleInterval = setInterval(() => {
          window.dispatchEvent(
            new MouseEvent("mousemove", {
              bubbles: true,
              cancelable: true,
              clientX: 0,
              clientY: 0,
            })
          );
        }, 2000);
      }
      if ((action === "pause" || action === "ended") && this.idleInterval) {
        clearInterval(this.idleInterval);
        this.idleInterval = null;
      }
    },

    ensureSDKsLoaded() {
      if (!window.Vimeo) {
        const s = document.createElement("script");
        s.src = "https://player.vimeo.com/api/player.js";
        document.head.appendChild(s);
      }
      if (!window.YT || !window.YT.Player) {
        const t = document.createElement("script");
        t.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(t);
      }
    },

    processChildDocument(doc) {
      const extractEmbedly = (ifr) => {
        try {
          const raw = ifr.src || ifr.dataset.src || "";
          const u = new URL(raw);
          return u.searchParams.get("src")
            ? decodeURIComponent(u.searchParams.get("src"))
            : null;
        } catch {
          return null;
        }
      };
      const copySizing = (src, dst) => {
        dst.width = src.width || src.clientWidth || 640;
        dst.height = src.height || src.clientHeight || 360;
        dst.style.cssText = src.style.cssText;
        dst.className = src.className;
      };
      const post = (p, a, id) =>
        window.postMessage({ platform: p, action: a, id }, "*");

      // Vimeo
      const attachVimeo = (ifr) => {
        if (ifr.__vhook) return;
        ifr.__vhook = true;
        const player = new Vimeo.Player(ifr);
        const vid = ifr.src.match(/video\/(\d+)/)?.[1] || null;
        player.on("play", () => post("vimeo", "play", vid));
        player.on("pause", () => post("vimeo", "pause", vid));
        player.on("ended", () => post("vimeo", "ended", vid));
      };
      const replaceEmbedlyVimeo = (ifr) => {
        const inner = extractEmbedly(ifr);
        if (!inner?.includes("player.vimeo.com/video")) return;
        const sep = inner.includes("?") ? "&" : "?";
        const url = inner + sep + "api=1&controls=1";
        const v = doc.createElement("iframe");
        v.src = url;
        v.frameBorder = 0;
        v.allow = "autoplay; fullscreen";
        copySizing(ifr, v);
        ifr.replaceWith(v);
        attachVimeo(v);
      };

      // YouTube
      const attachYouTube = (ifr) => {
        if (ifr.__ythook) return;
        ifr.__ythook = true;
        const tryInit = () => {
          if (window.YT && YT.Player) {
            new YT.Player(ifr, {
              events: {
                onStateChange: (e) => {
                  const id =
                    ifr.src.match(/embed\/([^?\s]+)/)?.[1] || null;
                  if (e.data === YT.PlayerState.PLAYING)
                    post("youtube", "play", id);
                  if (e.data === YT.PlayerState.PAUSED)
                    post("youtube", "pause", id);
                  if (e.data === YT.PlayerState.ENDED)
                    post("youtube", "ended", id);
                },
              },
            });
          } else {
            setTimeout(tryInit, 200);
          }
        };
        tryInit();
      };
      const replaceEmbedlyYouTube = (ifr) => {
        const inner = extractEmbedly(ifr);
        if (!inner?.includes("youtube.com/embed")) return;
        const m = inner.match(/embed\/([^?\s]+)/);
        if (!m) return;
        const id = m[1];
        const p = new URLSearchParams(inner.split("?")[1] || "");
        p.set("enablejsapi", "1");
        p.set("controls", p.get("controls") || "1");
        const url = `https://www.youtube.com/embed/${id}?${p}`;
        const y = doc.createElement("iframe");
        y.src = url;
        y.frameBorder = 0;
        y.allow = "autoplay; fullscreen";
        copySizing(ifr, y);
        ifr.replaceWith(y);
        attachYouTube(y);
      };

      // Native HTML5
      const attachNative = (v) => {
        if (v.__nhook) return;
        v.__nhook = true;
        const id = v.id || null;
        v.addEventListener("play", () => post("native", "play", id));
        v.addEventListener("pause", () => post("native", "pause", id));
        v.addEventListener("ended", () => post("native", "ended", id));
      };

      // 1) handle existing
      doc.querySelectorAll("iframe.embed__media").forEach((f) => {
        replaceEmbedlyVimeo(f);
        replaceEmbedlyYouTube(f);
      });
      doc
        .querySelectorAll('iframe[src*="player.vimeo.com/video"]')
        .forEach(attachVimeo);
      doc
        .querySelectorAll('iframe[src*="youtube.com/embed"]')
        .forEach(attachYouTube);
      doc.querySelectorAll("video").forEach(attachNative);

      // 2) OBSERVE ANY ADDITIONS OR ATTRIBUTE CHANGES
      if (!doc.__fullObserver) {
        doc.__fullObserver = true;
        const rootObs = new MutationObserver(() => {
          // re-run your existing logic on ANY mutation
          this.processChildDocument(doc);
        });
        const target = doc.documentElement || doc.body;
        if (target) {
          rootObs.observe(target, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["src", "data-src"],
          });
        }
      }
    },

    hookUserActivity(doc) {
      const evts = [
        "pointerdown","pointermove","pointerup","pointercancel",
        "wheel","scroll","keydown","keypress","keyup",
        "click","dblclick","contextmenu",
      ];
      evts.forEach((evt) =>
        [doc, doc.body].forEach((node) =>
          node.addEventListener(evt, () => this.handleIframeActivity(), true)
        )
      );
    },

    watchIframeTree(rootIframe) {
      const attachTo = (ifr) => {
        if (ifr.__watched) return;
        ifr.__watched = true;

        const doWork = () => {
          let doc;
          try {
            doc = ifr.contentWindow.document;
          } catch {
            return;
          }
          this.processChildDocument(doc);
          this.hookUserActivity(doc);
          Array.from(doc.querySelectorAll("iframe")).forEach(attachTo);
        };

        setTimeout(doWork, 200);
        ifr.addEventListener("load", doWork);
      };

      attachTo(rootIframe);
    },

    handleIframeActivity() {
      const now = Date.now();
      if (now - this._lastActivity < 2000) return;
      this._lastActivity = now;
      window.dispatchEvent(
        new MouseEvent("mousemove", { bubbles: true, cancelable: true })
      );
    },

    submitEmployeeData() {
      setTimeout(() => {
        const formatted = {
          is_last_lesson: 1,
          course_id: this.course_id,
          test_id: 0,
          percentageScored: window.API.cmi
            ? window.API.cmi.core.score.raw
            : 0,
          resultStatus: this.status,
          test_type: "scorm",
          isScorm: 1,
        };
        this.$emit("save-course-completion", formatted);
      }, 8000);
    },
  },
};
</script>

<style scoped>
iframe.scorm-player {
  height: 80vh;
  width: 100% !important;
}
</style>
